import {
  AssemblyTransformToolMode,
  MoveAlongEntitySelectionPhase,
  MoveComponentActivationSource,
  MoveComponentInputMode as MoveInputModeEnum,
  MoveComponentCollisionCheckScope,
  MoveComponentCollisionSelectionPhase,
  MoveComponentLifecycle,
  MoveComponentOptionMode as MoveOptionModeEnum,
  MoveComponentRotateInputMode,
} from './MoveComponentTypes';
import type {
  MoveComponentCollisionDetectionConfig,
  MoveComponentControllerOptions,
  MoveComponentInputMode,
  MoveComponentOptionMode,
  MoveComponentState,
  MoveComponentViewModel,
  MoveComponentXYZTuple,
} from './MoveComponentTypes';
import { Subject } from 'rxjs';
import type { Observable } from 'rxjs';
import * as THREE from 'three/webgpu';
import type { Viewer } from '../../viewer/Viewer';
import { ViewportCursorStyle } from '../../viewer/moudles/ViewportCursorManager';
import { MoveController } from './move/MoveController';
import { MoveService } from './move/MoveService';
import { RotateController } from './rotate/RotateController';
import { RotateService } from './rotate/RotateService';
import { recomputeAssemblyDatumReferences } from '../../reference/shared/recomputeAssemblyDatumReferences';
import {
  applyComponentInstanceSnapshotToObject,
  readComponentInstanceAssemblyNodeId,
} from '../component-instance';
import type {
  AssemblyTransformHistoryRecord,
  ComponentInstanceSnapshot,
} from '../component-instance';
import { resolveMoveComponentTransformTarget } from './MoveComponentTransformTargetResolver';

/**
 * 装配体 transform 历史提交选项。
 */
interface CommitAssemblyTransformHistoryRecordOptions {
  /**
   * 提交失败时的补偿回调。
   *
   * 说明：
   * 该回调必须在内部提交队列继续下一笔记录前执行，避免旧失败回滚晚于新事务。
   */
  readonly onCommitFailed?: (error: unknown, record: AssemblyTransformHistoryRecord) => void;
}

/**
 * 创建默认状态。
 *
 * @returns 默认状态快照。
 */
function createDefaultState(): MoveComponentState {
  return {
    active: false,
    lifecycle: MoveComponentLifecycle.Closed,
    activationSource: MoveComponentActivationSource.None,
    toolMode: AssemblyTransformToolMode.Move,
    moveInputMode: MoveInputModeEnum.FreeDrag,
    alongEntityReferenceObjectId: null,
    alongEntityReferenceLabel: null,
    alongEntityReferenceType: null,
    alongEntityConstraintKind: null,
    alongEntitySelectionPhase: MoveAlongEntitySelectionPhase.AwaitingReference,
    deltaXYZ: [0, 0, 0],
    targetXYZPosition: [0, 0, 0],
    rotateInputMode: MoveComponentRotateInputMode.FreeDrag,
    rotateDeltaXYZ: [0, 0, 0],
    optionMode: MoveOptionModeEnum.Standard,
    //------ 移动旋转零部件碰撞检查选项 开始------
    collisionCheckScope: MoveComponentCollisionCheckScope.AllComponents,
    collisionCheckComponentObjectIds: [],
    collisionSelectionPhase: MoveComponentCollisionSelectionPhase.Dragging,
    collisionStopAtCollision: false,
    collisionDraggedPartOnly: false,
    //------ 移动旋转零部件碰撞检查选项 结束------
  };
}

/**
 * 装配体移动零部件总控制器。
 *
 * 设计说明：
 * 1. 统一承接移动与旋转的状态管理；
 * 2. 将 UI 调用入口稳定为单一控制器接口；
 * 3. 子控制器负责各自行为，避免单类职责膨胀。
 */
export class MoveComponentController {
  private readonly viewer: Viewer;
  private readonly options: MoveComponentControllerOptions;
  private readonly moveController: MoveController;
  private readonly rotateController: RotateController;
  private state: MoveComponentState = createDefaultState();
  /** 当前等待提交或持久化的装配体 transform 历史记录数量。 */
  private assemblyHistoryCommitPendingCount = 0;
  private assemblyHistoryCommitQueue: Promise<void> = Promise.resolve();
  /** 装配体 transform 成功提交后的事件流，供引用重建等异步链路订阅。 */
  private readonly assemblyTransformCommittedSubject = new Subject<AssemblyTransformHistoryRecord>();

  /**
   * 创建移动零部件总控制器。
   *
   * @param viewer 视图实例。
   * @param options 控制器选项。
   */
  public constructor(viewer: Viewer, options: MoveComponentControllerOptions = {}) {
    this.viewer = viewer;
    this.options = options;

    const moveService = new MoveService(viewer);
    const rotateService = new RotateService(viewer);

    this.moveController = new MoveController(viewer, moveService, this.state.moveInputMode);
    this.rotateController = new RotateController(viewer, rotateService, this.state.rotateInputMode);
  }

  /**
   * 获取当前视图模型。
   *
   * @returns 视图模型。
   */
  public getViewModel(): MoveComponentViewModel {
    return {
      state: {
        ...this.state,
        collisionCheckComponentObjectIds: [...this.state.collisionCheckComponentObjectIds],
      },
      canSubmit: this.state.active,
    };
  }

  /**
   * 订阅装配体 transform 历史记录成功提交事件。
   *
   * 说明：
   * 该事件只在上层历史提交成功后发布，避免失败回滚时触发基于错误位姿的引用重建。
   *
   * @returns 成功提交后的装配体 transform 历史记录事件流。
   */
  public observeAssemblyTransformCommitted(): Observable<AssemblyTransformHistoryRecord> {
    return this.assemblyTransformCommittedSubject.asObservable();
  }

  /**
   * 判断工具是否激活。
   *
   * @returns 激活状态。
   */
  public isActive(): boolean {
    return this.state.active;
  }

  /**
   * 判断当前是否允许在视口中直接拖拽零部件。
   *
   * 设计说明：
   * 1. Assembly 空闲态会静默打开默认 Move 能力，并允许左键直接拖拽零部件；
   * 2. 显式“移动零部件 / 旋转零部件”命令同样允许视口直接拖拽；
   * 3. 默认能力只授权拖拽本身，不自动显示命令光标或 PropertyManager。
   *
   * @returns 是否允许视口直接拖拽零部件。
   */
  public canStartViewportDrag(requestedToolMode: AssemblyTransformToolMode = this.state.toolMode): boolean {
    //------ 装配体默认拖拽授权 开始------
    if (!this.state.active) {
      return false;
    }

    if (this.state.activationSource === MoveComponentActivationSource.Command) {
      return true;
    }

    if (this.state.activationSource === MoveComponentActivationSource.DefaultCapability) {
      return requestedToolMode === AssemblyTransformToolMode.Move
        || requestedToolMode === AssemblyTransformToolMode.Rotate;
    }

    return false;
    //------ 装配体默认拖拽授权 结束------
  }

  /**
   * 获取当前主模式（移动 / 旋转）。
   *
   * @returns 当前 toolMode。
   */
  public getToolMode(): AssemblyTransformToolMode {
    return this.state.toolMode;
  }

  /**
   * 打开控制器。
   *
   * @param toolMode 主模式。
   * @param activationSource 激活来源。
   */
  public open(
    toolMode: AssemblyTransformToolMode = AssemblyTransformToolMode.Move,
    activationSource: MoveComponentActivationSource = MoveComponentActivationSource.Command,
  ): void {
    const wasActive = this.state.active;
    const prevToolMode = this.state.toolMode;
    const previousActivationSource = this.state.activationSource;
    this.patchState({
      active: true,
      lifecycle: MoveComponentLifecycle.Open,
      activationSource,
      toolMode,
    });

    // 根据模式选择对应光标：移动用四向移动光标，旋转用十字准星。
    const nextCursor = toolMode === AssemblyTransformToolMode.Rotate
      ? ViewportCursorStyle.Crosshair
      : ViewportCursorStyle.Move;
    const prevCursor = prevToolMode === AssemblyTransformToolMode.Rotate
      ? ViewportCursorStyle.Crosshair
      : ViewportCursorStyle.Move;
    const shouldShowCommandCursor = activationSource === MoveComponentActivationSource.Command;
    const hadCommandCursor = wasActive && previousActivationSource === MoveComponentActivationSource.Command;

    if (shouldShowCommandCursor && !hadCommandCursor) {
      // 显式命令首次接管视口拖拽时才压入命令光标，静默默认能力不污染普通选择态。
      this.viewer.cursorManager.push(nextCursor);
    } else if (shouldShowCommandCursor && prevToolMode !== toolMode) {
      // 已激活但模式切换：替换栈中的旧光标。
      this.viewer.cursorManager.replace(prevCursor, nextCursor);
    } else if (!shouldShowCommandCursor && hadCommandCursor) {
      // 从显式命令退回静默能力时移除命令光标，避免光标状态泄漏。
      this.viewer.cursorManager.pop(prevCursor);
    }
  }

  /**
   * 静默打开装配体默认 Move 能力。
   *
   * 设计说明：
   * 1. 该入口仅用于装配模式空闲态的数据准备；
   * 2. 它会授权左键直接拖拽零部件，但不会显示命令光标或 PropertyManager；
   * 3. 用户点击工具栏“移动零部件 / 旋转零部件”时仍应调用 `open()` 进入显式命令态。
   */
  public activateDefaultMoveCapability(): void {
    this.open(AssemblyTransformToolMode.Move, MoveComponentActivationSource.DefaultCapability);
  }

  /**
   * 设置移动输入模式。
   *
   * @param mode 移动模式。
   */
  public setMoveInputMode(mode: MoveComponentInputMode): void {
    this.ensureOpen(AssemblyTransformToolMode.Move);
    this.moveController.setMoveInputMode(mode);
    const nextStatePatch: Partial<MoveComponentState> = {
      moveInputMode: mode,
    };

    //------ 装配体移动输入模式预填充 开始------
    // 进入“到 XYZ 位置”时，PropertyManager 应显示当前选中对象的装配体世界坐标，
    // 这样用户可以直接微调目标位置，而不是手工先查询当前位置。
    if (mode === MoveInputModeEnum.ToXYZPosition) {
      nextStatePatch.targetXYZPosition = this.moveController.readSelectedTargetWorldPosition();
    }
    //------ 装配体移动输入模式预填充 结束------

    if (mode !== MoveInputModeEnum.AlongEntity) {
      this.moveController.clearAlongEntityReference();
      nextStatePatch.alongEntityReferenceObjectId = null;
      nextStatePatch.alongEntityReferenceLabel = null;
      nextStatePatch.alongEntityReferenceType = null;
      nextStatePatch.alongEntityConstraintKind = null;
      nextStatePatch.alongEntitySelectionPhase = MoveAlongEntitySelectionPhase.AwaitingReference;
    } else {
      nextStatePatch.alongEntityReferenceObjectId = this.moveController.getAlongEntityReferenceObjectId();
      nextStatePatch.alongEntityReferenceLabel = this.moveController.getAlongEntityReferenceLabel();
      nextStatePatch.alongEntityReferenceType = this.moveController.getAlongEntityReferenceType();
      nextStatePatch.alongEntityConstraintKind = this.moveController.getAlongEntityConstraintKind();
      nextStatePatch.alongEntitySelectionPhase = this.moveController.getAlongEntitySelectionPhase();
    }

    this.patchState(nextStatePatch);
  }

  /**
   * 判断对象是否可作为沿实体参考对象。
   *
   * @param object 待判断对象。
   * @returns 是否允许作为参考对象。
   */
  public canUseAsAlongEntityReference(object: THREE.Object3D | null | undefined): boolean {
    return this.moveController.canUseAsAlongEntityReference(object);
  }

  /**
   * 判断当前是否已选中沿实体参考对象。
   *
   * @returns 是否存在参考对象。
   */
  public hasAlongEntityReference(): boolean {
    return this.moveController.hasAlongEntityReference();
  }

  /**
   * 写入沿实体参考对象。
   *
   * @param object 参考对象。
   * @returns 是否写入成功。
   */
  public setAlongEntityReference(object: THREE.Object3D | null): boolean {
    const success = this.moveController.setAlongEntityReference(object);
    if (!success) {
      return false;
    }

    this.patchState({
      alongEntityReferenceObjectId: this.moveController.getAlongEntityReferenceObjectId(),
      alongEntityReferenceLabel: this.moveController.getAlongEntityReferenceLabel(),
      alongEntityReferenceType: this.moveController.getAlongEntityReferenceType(),
      alongEntityConstraintKind: this.moveController.getAlongEntityConstraintKind(),
      alongEntitySelectionPhase: this.moveController.getAlongEntitySelectionPhase(),
    });
    return true;
  }

  /**
   * 清空沿实体参考对象。
   */
  public clearAlongEntityReference(): void {
    this.moveController.clearAlongEntityReference();
    this.patchState({
      alongEntityReferenceObjectId: null,
      alongEntityReferenceLabel: null,
      alongEntityReferenceType: null,
      alongEntityConstraintKind: null,
      alongEntitySelectionPhase: MoveAlongEntitySelectionPhase.AwaitingReference,
    });
  }

  /**
   * 读取当前沿实体参考对象。
   *
   * @returns 参考对象；未设置时返回 `null`。
   */
  public getAlongEntityReferenceObject(): THREE.Object3D | null {
    return this.moveController.getAlongEntityReference()?.object ?? null;
  }

  /**
   * 读取当前沿实体约束运行时快照。
   *
   * @returns 运行时快照；未设置时返回 `null`。
   */
  public getAlongEntityReference() {
    return this.moveController.getAlongEntityReference();
  }

  /**
   * 设置 Delta XYZ 输入值。
   *
   * @param delta 三轴增量（mm）。
   */
  public setDeltaXYZ(delta: MoveComponentXYZTuple): void {
    this.ensureOpen(AssemblyTransformToolMode.Move);
    this.patchState({ deltaXYZ: [...delta] as MoveComponentXYZTuple });
  }

  /**
   * 设置到 XYZ 位置输入值。
   *
   * @param position 装配体世界坐标（mm）。
   */
  public setTargetXYZPosition(position: MoveComponentXYZTuple): void {
    this.ensureOpen(AssemblyTransformToolMode.Move);
    this.patchState({ targetXYZPosition: [...position] as MoveComponentXYZTuple });
  }

  /**
   * 设置旋转输入模式。
   *
   * @param mode 旋转模式。
   */
  public setRotateInputMode(mode: MoveComponentRotateInputMode | string): void {
    this.ensureOpen(AssemblyTransformToolMode.Rotate);
    const normalizedMode = mode as MoveComponentRotateInputMode;
    this.rotateController.setRotateInputMode(normalizedMode);
    this.patchState({ rotateInputMode: normalizedMode });
  }

  /**
   * 设置旋转 Delta XYZ 输入值。
   *
   * @param deltaRotation 三轴旋转增量（度）。
   */
  public setRotateDeltaXYZ(deltaRotation: MoveComponentXYZTuple): void {
    this.ensureOpen(AssemblyTransformToolMode.Rotate);
    this.patchState({ rotateDeltaXYZ: [...deltaRotation] as MoveComponentXYZTuple });
  }

  /**
   * 设置选项模式。
   *
   * @param mode 选项模式。
   */
  public setOptionMode(mode: MoveComponentOptionMode): void {
    this.ensureOpen(this.state.toolMode);
    //------ 装配体碰撞控制收口 开始------
    // 碰撞检测仅由 MoveController 开关控制：
    // 1. CollisionDetection 开启；
    // 2. Standard 关闭。
    this.moveController.setCollisionEnabled(mode === MoveOptionModeEnum.CollisionDetection);
    //------ 装配体碰撞控制收口 结束------
    this.patchState({
      optionMode: mode,
      collisionSelectionPhase: mode === MoveOptionModeEnum.CollisionDetection
        ? this.state.collisionSelectionPhase
        : MoveComponentCollisionSelectionPhase.Dragging,
    });
  }

  //------ 移动旋转零部件碰撞检查选项 开始------
  /**
   * 设置碰撞检查范围。
   *
   * @param scope 碰撞检查范围。
   */
  public setCollisionCheckScope(scope: MoveComponentCollisionCheckScope): void {
    this.ensureOpen(this.state.toolMode);
    this.patchState({
      collisionCheckScope: scope,
      collisionSelectionPhase: scope === MoveComponentCollisionCheckScope.SelectedComponents
        ? MoveComponentCollisionSelectionPhase.SelectingComponents
        : MoveComponentCollisionSelectionPhase.Dragging,
    });
  }

  /**
   * 激活“供碰撞检查的零部件”选择框。
   */
  public activateCollisionCheckComponentSelection(): void {
    this.ensureOpen(this.state.toolMode);
    this.patchState({
      collisionCheckScope: MoveComponentCollisionCheckScope.SelectedComponents,
      collisionSelectionPhase: MoveComponentCollisionSelectionPhase.SelectingComponents,
    });
  }

  /**
   * 恢复移动/旋转拖动。
   */
  public resumeCollisionDrag(): void {
    this.ensureOpen(this.state.toolMode);
    this.patchState({
      collisionSelectionPhase: MoveComponentCollisionSelectionPhase.Dragging,
    });
  }

  /**
   * 判断当前是否处于碰撞检查零部件选择阶段。
   *
   * @returns 正在选择碰撞检查零部件时返回 `true`。
   */
  public isCollisionCheckComponentSelectionActive(): boolean {
    return this.state.active
      && this.state.optionMode === MoveOptionModeEnum.CollisionDetection
      && this.state.collisionCheckScope === MoveComponentCollisionCheckScope.SelectedComponents
      && this.state.collisionSelectionPhase === MoveComponentCollisionSelectionPhase.SelectingComponents;
  }

  /**
   * 追加一个供碰撞检查的零部件。
   *
   * @param object 零部件场景对象。
   */
  public addCollisionCheckComponentObject(object: THREE.Object3D): void {
    const targetObject = resolveMoveComponentTransformTarget(object, this.viewer) ?? object;
    const nextObjectIds = this.appendUniqueObjectId(
      this.state.collisionCheckComponentObjectIds,
      targetObject.uuid,
    );

    this.patchState({
      collisionCheckScope: MoveComponentCollisionCheckScope.SelectedComponents,
      collisionSelectionPhase: MoveComponentCollisionSelectionPhase.SelectingComponents,
      collisionCheckComponentObjectIds: nextObjectIds,
    });
  }

  /**
   * 移除一个供碰撞检查的零部件。
   *
   * @param objectId 零部件对象 ID。
   */
  public removeCollisionCheckComponentObjectId(objectId: string): void {
    if (!this.state.collisionCheckComponentObjectIds.includes(objectId)) {
      return;
    }

    this.patchState({
      collisionCheckComponentObjectIds: this.state.collisionCheckComponentObjectIds
        .filter((item) => item !== objectId),
    });
  }

  /**
   * 清空供碰撞检查的零部件列表。
   */
  public clearCollisionCheckComponentObjectIds(): void {
    if (this.state.collisionCheckComponentObjectIds.length === 0) {
      return;
    }

    this.patchState({
      collisionCheckComponentObjectIds: [],
    });
  }

  /**
   * 读取供碰撞检查的零部件对象。
   *
   * @returns 当前仍能在场景中解析到的零部件对象。
   */
  public getCollisionCheckComponentObjects(): THREE.Object3D[] {
    return this.state.collisionCheckComponentObjectIds
      .map((objectId) => this.viewer.findSceneObjectById(objectId))
      .filter((object): object is THREE.Object3D => Boolean(object));
  }

  /**
   * 设置是否在碰撞时停止。
   *
   * @param enabled 是否启用。
   */
  public setCollisionStopAtCollision(enabled: boolean): void {
    this.ensureOpen(this.state.toolMode);
    this.patchState({ collisionStopAtCollision: enabled });
  }

  /**
   * 设置是否仅检查被拖动的主零部件。
   *
   * @param enabled 是否启用。
   */
  public setCollisionDraggedPartOnly(enabled: boolean): void {
    this.ensureOpen(this.state.toolMode);
    this.patchState({ collisionDraggedPartOnly: enabled });
  }

  /**
   * 读取碰撞检查运行配置。
   *
   * @returns 当前碰撞检查配置快照。
   */
  public getCollisionDetectionConfig(): MoveComponentCollisionDetectionConfig {
    return {
      enabled: this.state.optionMode === MoveOptionModeEnum.CollisionDetection,
      scope: this.state.collisionCheckScope,
      componentObjectIds: [...this.state.collisionCheckComponentObjectIds],
      stopAtCollision: this.state.collisionStopAtCollision,
      draggedPartOnly: this.state.collisionDraggedPartOnly,
    };
  }
  //------ 移动旋转零部件碰撞检查选项 结束------

  /**
   * 读取当前碰撞检测开关状态。
   *
   * @returns 是否启用碰撞检测。
   */
  public isCollisionEnabled(): boolean {
    return this.state.optionMode === MoveOptionModeEnum.CollisionDetection
      && this.moveController.isCollisionEnabled();
  }

  /**
   * 判断是否存在尚未完成的装配体 transform 历史提交。
   *
   * 设计说明：
   * 视口拖拽的第二次 mouse down 不能建立在第一笔尚未持久化成功的 after 位姿上，
   * 否则第一笔失败时后续事务会引用一个从未入栈的 before 状态。
   *
   * @returns 存在待提交历史时返回 `true`。
   */
  public isAssemblyTransformHistoryCommitPending(): boolean {
    return this.assemblyHistoryCommitPendingCount > 0;
  }

  /**
   * 获取当前移动输入模式，供拖拽策略读取。
   *
   * @returns 当前移动输入模式。
   */
  public getMoveInputMode(): MoveComponentInputMode {
    return this.moveController.getMoveInputMode();
  }

  /**
   * 预览 Delta XYZ 移动。
   *
   * @param delta 三轴增量（mm）。
   */
  public previewMoveByDelta(delta: readonly [number, number, number]): void {
    this.ensureOpen(AssemblyTransformToolMode.Move);
    this.moveController.previewDeltaMove(delta);
  }

  /**
   * 按当前 Delta XYZ 输入值执行移动预览。
   */
  public applyDeltaXYZMove(): void {
    this.ensureOpen(AssemblyTransformToolMode.Move);
    this.moveController.previewDeltaMoveByAssemblyCoordinates(this.state.deltaXYZ);
  }

  /**
   * 预览移动到指定世界坐标。
   *
   * @param position 目标世界坐标（mm）。
   */
  public previewMoveToPosition(position: readonly [number, number, number]): void {
    this.ensureOpen(AssemblyTransformToolMode.Move);
    this.moveController.previewMoveToPosition(position);
  }

  /**
   * 按当前目标 XYZ 输入值执行位置预览。
   */
  public applyMoveToXYZPosition(): void {
    this.ensureOpen(AssemblyTransformToolMode.Move);
    this.moveController.previewMoveToPosition(this.state.targetXYZPosition);
  }

  /**
   * 预览 Delta XYZ 旋转。
   *
   * @param deltaRotation 旋转增量（度）。
   */
  public previewRotateByDelta(deltaRotation: readonly [number, number, number]): void {
    this.ensureOpen(AssemblyTransformToolMode.Rotate);
    this.rotateController.previewDeltaRotate(deltaRotation);
  }

  /**
   * 按当前旋转 Delta XYZ 输入值执行旋转预览。
   */
  public applyDeltaXYZRotate(): void {
    this.ensureOpen(AssemblyTransformToolMode.Rotate);
    this.rotateController.previewDeltaRotate(this.state.rotateDeltaXYZ);
  }

  /**
   * 提交本次操作。
   *
   * @throws 原样抛出装配体变换历史提交失败的异常。
   */
  public async commit(): Promise<void> {
    if (!this.state.active) {
      return;
    }
    const shouldPopCursor = this.state.activationSource === MoveComponentActivationSource.Command;
    const commitCursor = this.state.toolMode === AssemblyTransformToolMode.Rotate
      ? ViewportCursorStyle.Crosshair
      : ViewportCursorStyle.Move;

    // 统一收口提交链路，提交后保留当前预览位姿。
    const moveRecord = this.moveController.commitPreview();
    const rotateRecord = this.rotateController.commitPreview();
    try {
      await this.commitAssemblyTransformHistoryRecord(moveRecord, {
        onCommitFailed: () => this.rollbackAssemblyTransformHistoryRecord(moveRecord),
      });
      await this.commitAssemblyTransformHistoryRecord(rotateRecord, {
        onCommitFailed: () => this.rollbackAssemblyTransformHistoryRecord(rotateRecord),
      });
    } catch (error) {
      this.patchState({
        active: false,
        lifecycle: MoveComponentLifecycle.Cancelled,
        activationSource: MoveComponentActivationSource.None,
        alongEntityReferenceObjectId: null,
        alongEntityReferenceLabel: null,
        alongEntityReferenceType: null,
        alongEntityConstraintKind: null,
        alongEntitySelectionPhase: MoveAlongEntitySelectionPhase.AwaitingReference,
        collisionSelectionPhase: MoveComponentCollisionSelectionPhase.Dragging,
      });
      this.moveController.clearAlongEntityReference();
      if (shouldPopCursor) {
        this.viewer.cursorManager.pop(commitCursor);
      }
      throw error;
    }
    //------ 装配体基准引用移动后重算 开始------
    /**
     * 零部件位姿提交后，装配体内由组件拓扑创建的基准引用必须按新的组件世界位置重算。
     * 这里放在移动/旋转预览提交之后，确保所有引用读取到的是最终 matrixWorld。
     */
    recomputeAssemblyDatumReferences(this.viewer);
    //------ 装配体基准引用移动后重算 结束------

    this.patchState({
      active: false,
      lifecycle: MoveComponentLifecycle.Committed,
      activationSource: MoveComponentActivationSource.None,
      alongEntityReferenceObjectId: null,
      alongEntityReferenceLabel: null,
      alongEntityReferenceType: null,
      alongEntityConstraintKind: null,
      alongEntitySelectionPhase: MoveAlongEntitySelectionPhase.AwaitingReference,
      collisionSelectionPhase: MoveComponentCollisionSelectionPhase.Dragging,
    });
    this.moveController.clearAlongEntityReference();
    // 提交后恢复光标（根据当前模式弹出对应光标）。
    if (shouldPopCursor) {
      this.viewer.cursorManager.pop(commitCursor);
    }
  }

  /**
   * 取消本次操作。
   */
  public cancel(): void {
    const shouldPopCursor = this.state.activationSource === MoveComponentActivationSource.Command;
    const cancelCursor = this.state.toolMode === AssemblyTransformToolMode.Rotate
      ? ViewportCursorStyle.Crosshair
      : ViewportCursorStyle.Move;

    // 统一收口取消链路，确保对象位姿回滚。
    this.moveController.cancelPreview();
    this.rotateController.cancelPreview();

    this.patchState({
      active: false,
      lifecycle: MoveComponentLifecycle.Cancelled,
      activationSource: MoveComponentActivationSource.None,
      alongEntityReferenceObjectId: null,
      alongEntityReferenceLabel: null,
      alongEntityReferenceType: null,
      alongEntityConstraintKind: null,
      alongEntitySelectionPhase: MoveAlongEntitySelectionPhase.AwaitingReference,
      collisionSelectionPhase: MoveComponentCollisionSelectionPhase.Dragging,
    });
    this.moveController.clearAlongEntityReference();
    // 取消后恢复光标（根据当前模式弹出对应光标）。
    if (shouldPopCursor) {
      this.viewer.cursorManager.pop(cancelCursor);
    }
  }

  /**
   * 销毁控制器。
   */
  public dispose(): void {
    this.cancel();
    this.moveController.dispose();
    this.rotateController.dispose();
    this.assemblyTransformCommittedSubject.complete();
    void this.viewer;
  }

  /**
   * 提交视口直接拖拽产生的装配体 transform 历史记录。
   *
   * @param record 装配体 transform 历史记录。
   * @param commitOptions 提交补偿选项。
   */
  public async commitAssemblyTransformHistoryRecord(
    record: AssemblyTransformHistoryRecord | null,
    commitOptions: CommitAssemblyTransformHistoryRecordOptions = {},
  ): Promise<void> {
    if (!record) {
      return;
    }

    this.assemblyHistoryCommitPendingCount += 1;

    const queuedCommit = this.assemblyHistoryCommitQueue.then(async () => {
      try {
        await this.emitAssemblyTransformHistoryRecord(record);
        this.assemblyTransformCommittedSubject.next(record);
      } catch (error) {
        const failureHandler = commitOptions.onCommitFailed
          ?? (() => this.rollbackAssemblyTransformHistoryRecord(record));

        failureHandler(error, record);
        throw error;
      }
    }).finally(() => {
      /**
       * 该计数用于阻止用户在前序 mouseup 尚未持久化完成前继续拖拽同一装配体。
       * 失败和成功都必须释放计数；失败时真正的模型回滚由同一队列内的补偿回调完成。
       */
      this.assemblyHistoryCommitPendingCount = Math.max(0, this.assemblyHistoryCommitPendingCount - 1);
    });

    this.assemblyHistoryCommitQueue = queuedCommit.catch(() => undefined);

    await queuedCommit;
  }

  /**
   * 确保控制器已激活。
   *
   * @param toolMode 目标主模式。
   */
  private ensureOpen(toolMode: AssemblyTransformToolMode): void {
    if (this.state.active) {
      if (this.state.toolMode !== toolMode) {
        //------ 装配体模式切换光标修复 开始------
        // 已激活状态下切换主模式也必须走 open 链路，
        // 以触发光标替换（Move ↔ Rotate）。
        this.open(toolMode);
        //------ 装配体模式切换光标修复 结束------
      }
      return;
    }
    this.open(toolMode);
  }

  /**
   * 局部更新状态并触发通知。
   *
   * @param partial 局部状态。
   */
  private patchState(partial: Partial<MoveComponentState>): void {
    this.state = {
      ...this.state,
      ...partial,
    };
    this.emitStateChanged();
  }

  //------ 移动旋转零部件碰撞检查选项 开始------
  /**
   * 追加唯一对象 ID。
   *
   * @param source 原对象 ID 列表。
   * @param objectId 待追加对象 ID。
   * @returns 追加去重后的对象 ID 列表。
   */
  private appendUniqueObjectId(source: readonly string[], objectId: string): readonly string[] {
    if (source.includes(objectId)) {
      return source;
    }
    return [...source, objectId];
  }
  //------ 移动旋转零部件碰撞检查选项 结束------

  /**
   * 触发状态变化通知。
   */
  private emitStateChanged(): void {
    // 控制器状态与预览可能在同一输入内连续改变，画面统一由 Viewer 下一帧结算。
    this.viewer.requestRender();
  }

  /**
   * 发出装配体 transform 历史记录。
   *
   * @param record 装配体 transform 历史记录。
   */
  private async emitAssemblyTransformHistoryRecord(record: AssemblyTransformHistoryRecord): Promise<void> {
    await this.options.onAssemblyTransformCommitted?.(record);
  }

  /**
   * 回滚装配体 transform 历史记录。
   *
   * @param record 需要回滚的历史记录。
   */
  private rollbackAssemblyTransformHistoryRecord(record: AssemblyTransformHistoryRecord | null): void {
    if (!record) {
      return;
    }

    this.flattenAssemblyTransformHistoryRecords(record).forEach((item) => {
      this.applyAssemblyTransformSnapshot(item.before);
    });
  }

  /**
   * 展开同一次用户操作中的装配体 transform 历史记录。
   *
   * @param record 主历史记录。
   * @returns 主记录与联动记录的扁平集合。
   */
  private flattenAssemblyTransformHistoryRecords(
    record: AssemblyTransformHistoryRecord,
  ): readonly AssemblyTransformHistoryRecord[] {
    return [record, ...(record.relatedRecords ?? [])];
  }

  /**
   * 应用组件实例快照。
   *
   * @param snapshot 组件实例快照。
   */
  private applyAssemblyTransformSnapshot(snapshot: ComponentInstanceSnapshot): void {
    const targetObject = this.resolveAssemblyTransformObject(snapshot);

    if (!targetObject) {
      throw new Error(`无法回滚装配体组件位姿，场景中缺少组件实例：${snapshot.componentInstanceId}`);
    }

    applyComponentInstanceSnapshotToObject(targetObject, snapshot);
    recomputeAssemblyDatumReferences(this.viewer);
    this.viewer.updateImportedModelInstanceMatrices?.(targetObject);
    this.viewer.requestRender();
  }

  /**
   * 解析历史快照对应的场景对象。
   *
   * @param snapshot 组件实例快照。
   * @returns 场景对象；未找到时返回 null。
   */
  private resolveAssemblyTransformObject(snapshot: ComponentInstanceSnapshot): THREE.Object3D | null {
    const selectedHit = this.viewer.getSelectedObjects()
      .map((object) => resolveMoveComponentTransformTarget(object, this.viewer))
      .filter((object): object is THREE.Object3D => Boolean(object))
      .find((object) => this.isAssemblyTransformSnapshotTarget(object, snapshot));

    if (selectedHit) {
      return selectedHit;
    }

    const directHit = this.viewer.getSceneObjectById(snapshot.componentInstanceId)
      ?? this.viewer.getSceneObjectById(snapshot.sourceNodeId)
      ?? null;
    if (directHit) {
      return resolveMoveComponentTransformTarget(directHit, this.viewer);
    }

    let matchedObject: THREE.Object3D | null = null;
    this.viewer.getScene()?.traverse((object) => {
      if (matchedObject) {
        return;
      }

      if (this.isAssemblyTransformSnapshotTarget(object, snapshot)) {
        const transformRoot = resolveMoveComponentTransformTarget(object, this.viewer);
        if (transformRoot) {
          matchedObject = transformRoot;
        }
      }
    });

    return matchedObject;
  }

  /**
   * 判断场景对象是否对应历史快照中的组件实例。
   *
   * @param object 场景对象。
   * @param snapshot 组件实例快照。
   * @returns 是否命中同一个组件实例。
   */
  private isAssemblyTransformSnapshotTarget(
    object: THREE.Object3D,
    snapshot: ComponentInstanceSnapshot,
  ): boolean {
    const assemblyNodeId = readComponentInstanceAssemblyNodeId(object);

    return object.uuid === snapshot.componentInstanceId
      || object.name === snapshot.componentInstanceId
      || object.uuid === snapshot.sourceNodeId
      || object.name === snapshot.sourceNodeId
      || assemblyNodeId === snapshot.componentInstanceId
      || assemblyNodeId === snapshot.sourceNodeId;
  }
}
